/**
 * Cliente del Middleware de pagos (ENA-1607): `pre-initiate` e `initiate`.
 *
 * - **direct** (app / RN): `baseUrl` + Bearer + X-Customer-Session → NAT directo.
 * - **proxy** (checkout web): `checkout-proxy` del BFF VTEX + `X-Checkout-Session` (sin JWT en browser).
 *
 * `initiate` NO se reintenta nunca: ante timeout o corte de red el resultado es incierto
 * (`PaymentRequestError.outcomeUnknown`) y reintentar podría generar un cobro duplicado.
 */
import { PaymentRequestError } from '../domain/errors';
import type {
  ApplyGiftCardRequest,
  GiftCardCart,
  InitiateRequest,
  MercadoPagoInitiateRequest,
  MercadoPagoPreInitiateRequest,
  InitiateResponse,
  PaymentProviderName,
  PreInitiateRequest,
  PreInitiateResponse,
  SavedCardsProvider,
} from '../domain/models';

export interface InstallmentPromoSyncRequest {
  /** Una entrada por instrumento cacheado en pre-initiate; vacío limpia el tag gestionado. */
  installments: number[];
}

export interface DirectPaymentCredentials {
  mode?: 'direct';
  /** Origen del order-service (sin `/v2`), p. ej. `https://host`. */
  baseUrl: string;
  bearerToken: string;
  sessionToken: string;
}

export interface ProxyPaymentCredentials {
  mode: 'proxy';
  /** URL absoluta del checkout-proxy (sin `?path=`). */
  proxyBaseUrl: string;
  checkoutSessionId: string;
}

export type PaymentCredentials = DirectPaymentCredentials | ProxyPaymentCredentials;

export const isProxyPaymentCredentials = (
  credentials: PaymentCredentials,
): credentials is ProxyPaymentCredentials =>
  credentials.mode === 'proxy' ||
  ('proxyBaseUrl' in credentials &&
    'checkoutSessionId' in credentials &&
    !('bearerToken' in credentials && (credentials as DirectPaymentCredentials).bearerToken));

/** Respuesta de `POST …/checkout/payment/cards/display` (tema visual por BIN). */
export interface CardDisplayResponse {
  issuerLabel: string;
  networkLabel: string;
  networkVariant: string;
  colorStart: string;
  colorEnd: string;
  textColor: string;
  icon: string;
}

export interface PaymentMiddlewareRequestOptions {
  signal?: AbortSignal;
}

export interface PaymentMiddlewareClient {
  getSavedCards(): Promise<SavedCardsProvider[]>;
  /** Pintado de tarjeta al tipear BIN; fallos → `null` (tema neutro en UI). */
  displayCard(bin: string): Promise<CardDisplayResponse | null>;
  preInitiate(
    request: PreInitiateRequest | MercadoPagoPreInitiateRequest,
    requestOptions?: PaymentMiddlewareRequestOptions,
  ): Promise<PreInitiateResponse>;
  /** Actualiza el marketing tag de cuotas sin re-resolver routings (requiere pre-initiate previo). */
  syncInstallmentPromo(
    request: InstallmentPromoSyncRequest,
    requestOptions?: PaymentMiddlewareRequestOptions,
  ): Promise<PreInitiateResponse>;
  initiate(request: InitiateRequest | MercadoPagoInitiateRequest): Promise<InitiateResponse>;
  /** Elimina una tarjeta guardada (`DELETE .../payment/cards/{gateway}/{token}`). */
  deleteCard(gateway: PaymentProviderName, cardToken: string): Promise<void>;
  /** Aplica una gift card al carrito (`POST .../carts/gift-cards`); devuelve el carrito actualizado. */
  applyGiftCard(request: ApplyGiftCardRequest): Promise<GiftCardCart>;
  /** Quita una gift card del carrito (`DELETE .../carts/gift-cards`); devuelve el carrito actualizado. */
  removeGiftCard(giftCardId: string): Promise<GiftCardCart>;
}

export interface PaymentMiddlewareClientOptions {
  fetchImpl?: typeof fetch;
  savedCardsTimeoutMs?: number;
  cardDisplayTimeoutMs?: number;
  deleteCardTimeoutMs?: number;
  giftCardTimeoutMs?: number;
  preInitiateTimeoutMs?: number;
  initiateTimeoutMs?: number;
}

export const PAYMENT_PATHS = {
  savedCards: '/v2/order/checkout/payment/card',
  // Singular, igual que el GET: `.../payment/cards/...` lo corta el gateway con 404 (`CARREFOUR-HTTP 404`),
  // mientras que `.../payment/card/{gateway}/{token}` llega al payment-service (verificado en INT).
  cards: '/v2/order/checkout/payment/card',
  giftCards: '/v2/order/carts/gift-cards',
  cardDisplay: '/v2/order/checkout/payment/cards/display',
  preInitiate: '/v2/order/checkout/payment/pre-initiate',
  installmentPromoSync: '/v2/order/checkout/payment/installment-promo/sync',
  initiate: '/v2/order/checkout/payment/initiate',
} as const;

const DEFAULT_SAVED_CARDS_TIMEOUT_MS = 15_000;
const DEFAULT_DELETE_CARD_TIMEOUT_MS = 15_000;
const DEFAULT_GIFT_CARD_TIMEOUT_MS = 20_000;
const DEFAULT_CARD_DISPLAY_TIMEOUT_MS = 10_000;
const DEFAULT_PRE_INITIATE_TIMEOUT_MS = 30_000;
const DEFAULT_INITIATE_TIMEOUT_MS = 60_000;

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

const DECIMAL_MARK = '__decimal__';

/**
 * JSON con los montos (`totalAmount`, `amount`) escritos con dos decimales (`163799.00`). `JSON.stringify`
 * descarta el `.00` de un número entero y backend (`start-transaction-service`) respondió 400 con montos
 * sin decimales. Solo formatea el wire; los montos ya vienen normalizados en los builders de request.
 */
export const stringifyWithDecimalAmounts = (body: unknown): string =>
  JSON.stringify(body, (key, value) =>
    (key === 'totalAmount' || key === 'amount') && typeof value === 'number' && Number.isFinite(value)
      ? `${DECIMAL_MARK}${value.toFixed(2)}`
      : value,
  ).replace(new RegExp(`"${DECIMAL_MARK}(-?\\d+\\.\\d{2})"`, 'g'), '$1');

const readErrorDetail = async (response: Response): Promise<string> => {
  try {
    const text = await response.clone().text();
    const scrub = (value: string): string =>
      value
        .replace(/[0-9a-f]{32,}/gi, '[hex]')
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[uuid]')
        .slice(0, 300);
    try {
      const parsed = JSON.parse(text) as { errors?: Array<{ code?: string; title?: string }> };
      if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
        return scrub(parsed.errors.map((e) => `${e.code ?? '?'} | ${e.title ?? '?'}`).join(' ; '));
      }
    } catch {
      // no es JSON
    }
    return scrub(text);
  } catch {
    return '';
  }
};

export const createPaymentMiddlewareClient = (
  credentials: PaymentCredentials,
  options: PaymentMiddlewareClientOptions = {},
): PaymentMiddlewareClient => {
  const fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
  const proxyMode = isProxyPaymentCredentials(credentials);
  const directBaseUrl = proxyMode
    ? ''
    : credentials.baseUrl.replace(/\/+$/, '');
  const proxyBaseUrl = proxyMode ? credentials.proxyBaseUrl.replace(/\/+$/, '') : '';

  const buildUrl = (path: string): string => {
    if (proxyMode) {
      return `${proxyBaseUrl}?path=${encodeURIComponent(path)}`;
    }
    return `${directBaseUrl}${path}`;
  };

  const buildHeaders = (hasJsonBody: boolean): Record<string, string> => {
    if (proxyMode) {
      return {
        ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
        'X-Checkout-Session': credentials.checkoutSessionId,
        'X-Client-Channel': 'WEB',
      };
    }
    return {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${credentials.bearerToken}`,
      'X-Customer-Session': credentials.sessionToken,
      'X-Client-Channel': 'APP',
    };
  };

  const send = async <TBody, TResponse>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    body: TBody | undefined,
    timeoutMs: number,
    outcomeUncertainOnFailure: boolean,
    /** `false` = la respuesta exitosa no trae cuerpo (p. ej. 204). */
    expectBody = true,
    serialize: (value: unknown) => string = JSON.stringify,
    requestSignal?: AbortSignal,
  ): Promise<TResponse> => {
    if (requestSignal?.aborted) {
      throw new PaymentRequestError('Request cancelled', {
        failure: 'cancelled',
        outcomeUnknown: false,
      });
    }

    const controller = new AbortController();
    let cancelledByCaller = false;
    const onCallerAbort = () => {
      cancelledByCaller = true;
      controller.abort();
    };
    requestSignal?.addEventListener('abort', onCallerAbort);

    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const hasJsonBody = body !== undefined;

    let response: Response;
    try {
      response = await fetchImpl(buildUrl(path), {
        method,
        headers: buildHeaders(hasJsonBody),
        ...(hasJsonBody ? { body: serialize(body) } : {}),
        signal: controller.signal,
      });
    } catch (error) {
      if (cancelledByCaller || requestSignal?.aborted) {
        throw new PaymentRequestError('Request cancelled', {
          failure: 'cancelled',
          outcomeUnknown: false,
        });
      }
      const timedOut = isAbortError(error);
      throw new PaymentRequestError(timedOut ? 'Request timeout' : 'Network error', {
        failure: timedOut ? 'timeout' : 'network',
        outcomeUnknown: outcomeUncertainOnFailure,
      });
    } finally {
      requestSignal?.removeEventListener('abort', onCallerAbort);
      clearTimeout(timer);
    }

    if (!response.ok) {
      const detail = await readErrorDetail(response);
      throw new PaymentRequestError(`HTTP ${response.status}`, {
        failure: 'http',
        status: response.status,
        detail,
        outcomeUnknown: outcomeUncertainOnFailure && response.status >= 500,
      });
    }

    if (!expectBody) {
      return undefined as TResponse;
    }

    try {
      return (await response.json()) as TResponse;
    } catch {
      throw new PaymentRequestError('Invalid response body', {
        failure: 'bad-response',
        status: response.status,
        outcomeUnknown: outcomeUncertainOnFailure,
      });
    }
  };

  return {
    getSavedCards: () =>
      send<never, SavedCardsProvider[]>(
        'GET',
        PAYMENT_PATHS.savedCards,
        undefined,
        options.savedCardsTimeoutMs ?? DEFAULT_SAVED_CARDS_TIMEOUT_MS,
        false,
      ),
    displayCard: async (bin) => {
      try {
        const display = await send<{ bin: string }, CardDisplayResponse>(
          'POST',
          PAYMENT_PATHS.cardDisplay,
          { bin },
          options.cardDisplayTimeoutMs ?? DEFAULT_CARD_DISPLAY_TIMEOUT_MS,
          false,
        );
        if (!display?.colorStart || !display.colorEnd) return null;
        return display;
      } catch {
        return null;
      }
    },
    deleteCard: (gateway, cardToken) =>
      send<never, void>(
        'DELETE',
        `${PAYMENT_PATHS.cards}/${encodeURIComponent(gateway)}/${encodeURIComponent(cardToken)}`,
        undefined,
        options.deleteCardTimeoutMs ?? DEFAULT_DELETE_CARD_TIMEOUT_MS,
        false,
        false,
      ),
    applyGiftCard: (request) =>
      send<ApplyGiftCardRequest, GiftCardCart>(
        'POST',
        PAYMENT_PATHS.giftCards,
        request,
        options.giftCardTimeoutMs ?? DEFAULT_GIFT_CARD_TIMEOUT_MS,
        false,
      ),
    removeGiftCard: (giftCardId) =>
      send<{ giftCardId: string }, GiftCardCart>(
        'DELETE',
        PAYMENT_PATHS.giftCards,
        { giftCardId },
        options.giftCardTimeoutMs ?? DEFAULT_GIFT_CARD_TIMEOUT_MS,
        false,
      ),
    preInitiate: (request, requestOptions) =>
      send<PreInitiateRequest | MercadoPagoPreInitiateRequest, PreInitiateResponse>(
        'POST',
        PAYMENT_PATHS.preInitiate,
        request,
        options.preInitiateTimeoutMs ?? DEFAULT_PRE_INITIATE_TIMEOUT_MS,
        false,
        true,
        stringifyWithDecimalAmounts,
        requestOptions?.signal,
      ),
    syncInstallmentPromo: (request, requestOptions) =>
      send<InstallmentPromoSyncRequest, PreInitiateResponse>(
        'POST',
        PAYMENT_PATHS.installmentPromoSync,
        request,
        options.preInitiateTimeoutMs ?? DEFAULT_PRE_INITIATE_TIMEOUT_MS,
        false,
        true,
        JSON.stringify,
        requestOptions?.signal,
      ),
    initiate: (request) =>
      send<InitiateRequest | MercadoPagoInitiateRequest, InitiateResponse>(
        'POST',
        PAYMENT_PATHS.initiate,
        request,
        options.initiateTimeoutMs ?? DEFAULT_INITIATE_TIMEOUT_MS,
        true,
        true,
        stringifyWithDecimalAmounts,
      ),
  };
};
